import React, { useContext, useEffect } from 'react';
import NvIcon from 'shared/components/nv-icon';
import { QuizQuestionType } from 'redux/schemas/models/quiz';
import QuestionSetContext, { QuestionContext, SavingIndicator } from 'lecture_pages/services/question-set-context';
import { useDispatch } from 'react-redux';
import { wrapThunkAction } from 'redux/utils';
import { editNumberAnswer } from 'redux/actions/quizzes';
import NumberAnswerSettingInput from './number-answer-setting-input';

type Limits = {
  maxLimit: number;
  minLimit: number;
};

type NumberAnswerSettingContainerProps = {
  setTriggerShowFeedback: (triggerShowFeedback: boolean) => void;
  isAnswerInRange: boolean
  setIsAnswerInRange: Function
  topOfRange: string
  bottomOfRange: string
  setBottomOfRange: Function
  setTopOfRange: Function
  showFeedbackIcon: boolean
  limits?: Limits
};

const NumberAnswerSettingContainer = (props: NumberAnswerSettingContainerProps) => {
  const {
    setTriggerShowFeedback,
    isAnswerInRange,
    setIsAnswerInRange,
    topOfRange,
    bottomOfRange,
    setTopOfRange,
    setBottomOfRange,
    showFeedbackIcon,
    limits,
  } = props;

  const dispatch = useDispatch();
  const {
    currentQuestion,
    setSavingStatus,
    savingIndicatorTimeoutRef,
  } = useContext(QuestionSetContext);

  const {
    responseOptions,
  } = useContext(QuestionContext);

  const handleEditNumberAnswer = (bottomOfRangeParam: string, topOfRangeParam: string) => {
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);
    wrapThunkAction(dispatch(editNumberAnswer(
      {
        questionOptionId: responseOptions[0].id,
        bottomOfRange: bottomOfRangeParam,
        topOfRange: topOfRangeParam,
      },
    ))).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  useEffect(() => {
    if (currentQuestion.type === QuizQuestionType.LIKERT_SCALE_QUESTION) {
      const isBottomRangeOutOfLimit = !(limits.minLimit <= parseInt(bottomOfRange, 10) && limits.maxLimit >= parseInt(bottomOfRange, 10))
        && !!bottomOfRange;
      const isTopRangeOutOfLimit = !(limits.minLimit <= parseInt(topOfRange, 10) && limits.maxLimit >= parseInt(topOfRange, 10))
        && !!topOfRange;

      // when ranges are out of the limit, setting the corresponding range empty
      if (isBottomRangeOutOfLimit || isTopRangeOutOfLimit) {
        setBottomOfRange(isBottomRangeOutOfLimit ? '' : bottomOfRange);
        setTopOfRange(isTopRangeOutOfLimit ? '' : topOfRange);
        handleEditNumberAnswer(isBottomRangeOutOfLimit ? '' : bottomOfRange, isTopRangeOutOfLimit ? '' : topOfRange);
      }
    }
    // need to update only when limit is changed
  }, [limits, currentQuestion.type]);

  return (
    <div className='mt-2 number-setting-container d-flex'>
      <NumberAnswerSettingInput
        setTriggerShowFeedback={setTriggerShowFeedback}
        isAnswerInRange={isAnswerInRange}
        setIsAnswerInRange={setIsAnswerInRange}
        topOfRange={topOfRange}
        bottomOfRange={bottomOfRange}
        setBottomOfRange={setBottomOfRange}
        setTopOfRange={setTopOfRange}
        showFeedbackIcon={showFeedbackIcon}
        isBottomRange
      />
      {isAnswerInRange && (
      <React.Fragment>
        <div className='dash-component d-flex align-items-center justify-content-center'>
          <NvIcon icon='dash' size='small' className='d-flex align-items-center text-gray-4' />
        </div>
        <NumberAnswerSettingInput
          setTriggerShowFeedback={setTriggerShowFeedback}
          isAnswerInRange={isAnswerInRange}
          setIsAnswerInRange={setIsAnswerInRange}
          topOfRange={topOfRange}
          bottomOfRange={bottomOfRange}
          setBottomOfRange={setBottomOfRange}
          setTopOfRange={setTopOfRange}
          showFeedbackIcon={showFeedbackIcon}
          isBottomRange={false}
        />
      </React.Fragment>
      )}
    </div>
  );
};

export default NumberAnswerSettingContainer;
